import { BACKEND_URL } from "@/lib/api";
import { useEffect, useRef, useState } from "react";
import { m } from "framer-motion";
import { ArrowRight, Star, Sparkles, Flame, HeartPulse, Users, Check } from "lucide-react";
import { CourseFinder } from "./CourseFinder";
import { HERO_VIDEO, selectTopic } from "../../data/content";
import { useContent } from "../../content/ContentContext";
import { useGoogleReviews } from "@/lib/googleReviews";
import { SiteImage } from "./SiteImage";
import { VoucherBanner } from "./VoucherBanner";

const buildPaths = (img) => [
  { id: "einsteigen", icon: Sparkles, badge: "Neu oder nach einer Pause", title: "Anfangen oder wieder einsteigen", claim: "Wir erklären dir jede Übung – du musst vorher nichts können.", cta: "Kategorie wählen", topic: "einsteiger", info: ["So läuft das Probetraining", "/probetraining-flensburg"], img: img.einsteigen, pos: "28% 20%", alt: "Lachende Einsteigerin auf dem BikeErg im Kurs bei FlensFitness" },
  { id: "fitbleiben", icon: HeartPulse, badge: "Regelmäßig etwas für dich tun", title: "Fit bleiben im Alltag", claim: "Feste Termine, bei denen dich jemand erwartet – so oft, wie es in dein Leben passt.", cta: "Kategorie wählen", topic: "einsteiger", info: ["Alle Kurse ansehen", "/kurse"], img: img.coaching_kurs, pos: "64% 30%", alt: "Mitglied lacht beim Training mit der Hantelscheibe im gecoachten Kurs" },
  { id: "aktiv-stabil", icon: Users, badge: "Ruhiges Tempo · Aktiv & Stabil", title: "Beweglich und stabil bleiben", claim: "Kraft und Gleichgewicht in ruhigem Tempo – beliebt bei 60+.", cta: "Kategorie wählen", topic: "einsteiger", course: "Aktiv & Stabil", info: ["Mehr zu Aktiv & Stabil", "/fitness-fuer-senioren-flensburg"], img: img.aktiv, pos: "38% 25%", alt: "Lutz, seit 2017 Mitglied bei FlensFitness, trainiert am Kinesis-Zuggerät im Kurs Aktiv & Stabil" },
  { id: "sportlich", icon: Flame, badge: "Offizielles HYROX- & ATHX-Gym", title: "Sportlich weiterkommen", claim: "HYROX, ATHX und Kraftpur nach eigenem Programming – mit Coach messbar besser.", cta: "Kategorie wählen", topic: "performance", info: ["Mehr zu HYROX & ATHX", "/hyrox-flensburg"], img: img.leistung, pos: "42% 22%", alt: "Coach Sören und Athletin aus dem FlensFitness-Team an der Startlinie beim ATHX Kopenhagen 2026" },
];

const ease = [0.22, 1, 0.36, 1];

const PathCard = ({ p, active, onStart }) => {
  return (
    <m.article data-testid={`hero-path-${p.id}`} data-active={active || undefined}
      role="button" tabIndex={0} aria-pressed={active} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onStart(p.id); } }}
      className={`group card card-hover overflow-hidden flex flex-col cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#BA00C5] ${active ? "ring-2 ring-[#BA00C5]" : ""}`} onClick={() => onStart(p.id)}>
      <div className="relative aspect-[16/10] sm:aspect-[4/3] overflow-hidden bg-[#E6E3DD]">
        <SiteImage src={p.img} alt={p.alt} loading="eager" data-testid={`hero-image-${p.id}`} sizes="(min-width: 1024px) 25vw, (min-width: 640px) 45vw, 35vw" style={p.pos ? { objectPosition: p.pos } : undefined} className="absolute inset-0 w-full h-full object-cover object-[center_20%] transition-transform duration-700 ease-out group-hover:scale-[1.04]" />
      </div>
      <div className="p-4 sm:p-6 flex flex-col flex-1 min-w-0">
        <h3 data-testid={`hero-title-${p.id}`} className="h-display text-lg leading-tight sm:text-xl">{p.title}</h3>
        <p className="mt-1.5 sm:mt-2 text-[13px] sm:text-sm md:text-[15px] muted leading-snug sm:leading-relaxed flex-1">{p.claim}</p>
        <span data-testid={`hero-cta-${p.id}`} className={`mt-3 sm:mt-4 inline-flex items-center gap-1.5 font-head text-sm font-semibold ${active ? "text-[#BA00C5]" : "text-[#161616] group-hover:text-[#8A0093]"}`}>
          {active ? <><Check size={16} /> Ausgewählt</> : <>Weiter <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" /></>}
        </span>
      </div>
    </m.article>
  );
};

const useTodayCount = () => {
  const [count, setCount] = useState(null);
  useEffect(() => {
    fetch(`${BACKEND_URL}/api/kursplan`).then((r) => (r.ok ? r.json() : null)).then((d) => {
      if (!d) return;
      const now = new Date().toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Berlin" });
      const upcoming = (day) => (day?.items || []).filter((i) => !i.canceled && i.kind === "course" && (day.date > d.today || i.end_time > now));
      const day = d.days.filter((x) => x.date >= d.today).find((x) => upcoming(x).length > 0);
      if (!day) return setCount(null);
      setCount({ n: upcoming(day).length, label: day.date === d.today ? "heute noch" : `am ${new Date(`${day.date}T12:00:00`).toLocaleDateString("de-DE", { weekday: "long" })}` });
    }).catch(() => {});
  }, []);
  return count;
};

const useAfterLoad = () => {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const conn = navigator.connection;
    if (conn && (conn.saveData || /(^|-)2g$/.test(conn.effectiveType || ""))) return undefined;
    const go = () => setTimeout(() => setReady(true), 300);
    if (document.readyState === "complete") go(); else window.addEventListener("load", go, { once: true });
    const fallback = setTimeout(() => setReady(true), 2500);
    return () => { window.removeEventListener("load", go); clearTimeout(fallback); };
  }, []);
  return ready;
};

const HeroVideo = () => {
  const reduced = typeof window !== "undefined" && (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches || navigator.connection?.saveData || /(^|-)2g$/.test(navigator.connection?.effectiveType || ""));
  const ready = useAfterLoad();
  const ref = useRef(null);
  useEffect(() => {
    const v = ref.current;
    if (!v || !ready) return;
    v.muted = true; v.defaultMuted = true; v.loop = true;
    const play = () => { const p = v.play(); if (p?.catch) p.catch(() => {}); };
    const onEnded = () => { v.currentTime = 0; play(); };
    const onVisible = () => { if (document.visibilityState === "visible") play(); };
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) play(); else v.pause(); }, { threshold: 0.1 });
    v.addEventListener("ended", onEnded);
    v.addEventListener("stalled", play);
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("pageshow", play);
    // Low-power modes block autoplay until the first interaction.
    const kick = () => { if (v.paused) play(); };
    ["touchstart", "scroll", "pointerdown", "keydown"].forEach((t) => window.addEventListener(t, kick, { once: true, passive: true }));
    v.addEventListener("canplay", kick);
    io.observe(v);
    play();
    return () => { v.removeEventListener("ended", onEnded); v.removeEventListener("stalled", play); document.removeEventListener("visibilitychange", onVisible); window.removeEventListener("pageshow", play); v.removeEventListener("canplay", kick); ["touchstart", "scroll", "pointerdown", "keydown"].forEach((t) => window.removeEventListener(t, kick)); io.disconnect(); };
  }, [ready]);
  if (reduced) return <img src={HERO_VIDEO.poster} alt="Lachende Teilnehmerin beim gecoachten Kurs bei FlensFitness" width={720} height={900} fetchpriority="high" data-testid="hero-main-image" className="w-full h-full object-cover object-[center_30%]" />;
  return (
    <video ref={ref} key={ready ? "src" : "poster"} data-testid="hero-video" onClick={(e) => e.currentTarget.play()?.catch?.(() => {})} className="w-full h-full object-cover object-[center_30%]" autoPlay muted loop playsInline disableRemotePlayback preload={ready ? "auto" : "none"} poster={HERO_VIDEO.poster} aria-label="Kurzer Einblick in Kurse und Training bei FlensFitness">
      {ready && <source src={window.matchMedia("(max-width: 1023px)").matches ? HERO_VIDEO.mobile : HERO_VIDEO.desktop} type="video/mp4" />}
    </video>
  );
};

const useIsDesktop = () => {
  const q = "(min-width: 1024px)";
  const [d, setD] = useState(() => typeof window === "undefined" || window.matchMedia(q).matches);
  useEffect(() => {
    const mq = window.matchMedia(q);
    const on = () => setD(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return d;
};

const Headline = ({ texts, light = false }) => (
  <>
    <p className={`eyebrow mb-4 ${light ? "!text-white/85 !text-xs" : ""}`} data-testid="hero-eyebrow">Gecoachtes Gruppentraining in Flensburg</p>
    <h1 data-testid="hero-headline" className={`h-display text-4xl sm:text-5xl lg:text-[3.4rem] xl:text-[3.75rem] lg:leading-[1.04] ${light ? "text-white" : ""}`}>
      {texts.hero_title}<br /><span className={light ? "text-[#F3C8F6]" : "accent"}>{texts.hero_accent}</span>
    </h1>
  </>
);

const MobileHeroBg = ({ texts, reviews: cms }) => {
  const google = useGoogleReviews();
  const rating = google?.rating ?? cms.rating;
  return (
    <div data-testid="hero-mobile-bg" className="relative mx-3 mt-3 rounded-[28px] overflow-hidden bg-[#161616] min-h-[430px] sm:min-h-[520px] flex flex-col justify-end p-5 pb-6 sm:p-8">
      <div className="absolute inset-0"><HeroVideo /></div>
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/45 to-black/10" />
      <a href={google?.maps_url || cms.google_url} target="_blank" rel="noopener noreferrer" data-testid="hero-google-badge" className="absolute right-3 top-3 bg-white/95 rounded-full px-3 py-1.5 flex items-center gap-1.5 text-xs font-head font-bold">
        <Star size={12} className="fill-[#F2B705] text-[#F2B705]" />{String(rating).replace(".", ",")}<span className="font-body font-normal muted">Google</span>
      </a>
      <div className="relative"><Headline texts={texts} light /></div>
    </div>
  );
};

const HeroVisual = ({ reviews: cms }) => {
  const google = useGoogleReviews();
  const reviews = google ? { google_url: google.maps_url || cms.google_url, rating: google.rating ?? cms.rating, count: cms.count } : cms;
  const todayCount = useTodayCount();
  return (
    <div className="relative h-full min-h-[600px]" data-testid="hero-visual">
      <div className="absolute inset-0 rounded-bl-[48px] overflow-hidden bg-[#161616]">
        <HeroVideo />
        <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/5 to-black/20" />
        <a href={reviews.google_url} target="_blank" rel="noopener noreferrer" data-testid="hero-google-badge"
          className="absolute left-8 top-8 rounded-full bg-white/95 backdrop-blur px-4 py-2.5 flex items-center gap-2.5 text-sm shadow-sm hover:bg-white transition-colors">
          <span className="font-head font-bold text-base">{String(reviews.rating).replace(".", ",")}</span>
          <span className="flex gap-0.5">{[1, 2, 3, 4, 5].map((n) => <Star key={n} size={13} className="fill-[#F2B705] text-[#F2B705]" />)}</span>
          <span data-testid="hero-google-count" className="text-xs muted">{reviews.count ? `${reviews.count}+ Google-Bewertungen` : "Google"}</span>
        </a>
        <div className="absolute left-8 right-8 bottom-8 flex flex-wrap items-end justify-between gap-4 text-white">
          <div>
            <p className="font-head text-xs font-semibold uppercase tracking-[0.16em] text-white/70">Ein Blick in unsere Kurse</p>
            <p className="h-display text-2xl mt-1.5">Gemeinsam ist es leichter.</p>
          </div>
          {todayCount !== null && (
            <div data-testid="hero-today-badge" className="glass-dark rounded-full px-4 py-2.5 flex items-center gap-2.5">
              <span className="relative flex h-2.5 w-2.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#9BE39B] opacity-75" /><span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#9BE39B]" /></span>
              <span className="text-sm"><span className="font-head font-bold">{todayCount.n} Kurse</span> <span className="text-white/75">{todayCount.label}</span></span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const scrollToFinder = (e) => { e.preventDefault(); document.getElementById("kursfinder")?.scrollIntoView({ behavior: "smooth", block: "start" }); };

export const Hero = () => {
  const { texts, reviews } = useContent();
  const desktop = useIsDesktop();
  return (
    <section data-testid="hero-section" className="relative pt-[72px] bg-[#F7F6F3]">
      <VoucherBanner />
      {texts.announcement && (
        <div data-testid="announcement-bar" className="relative bg-[#161616] text-white text-center text-sm py-2.5 px-4">
          {texts.announcement_link ? <a href={texts.announcement_link} rel="noopener noreferrer" className="underline underline-offset-4 hover:text-[#E9B3EE]">{texts.announcement}</a> : texts.announcement}
        </div>
      )}
      {!desktop && <MobileHeroBg texts={texts} reviews={reviews} />}
      <div className="relative lg:grid lg:grid-cols-[1.25fr_0.75fr] lg:min-h-[min(calc(100svh-72px),860px)]">
        <div className="min-w-0 flex flex-col justify-center px-5 md:px-10 lg:pl-[max(2.5rem,calc((100vw_-_1320px)/2_+_2.5rem))] lg:pr-16 xl:pr-24 pt-7 pb-12 lg:py-16">
          {desktop && <Headline texts={texts} />}
          <p data-testid="hero-subline" className="lg:mt-8 text-[15px] md:text-lg text-[#3A3A3E] leading-relaxed max-w-[31rem]">{texts.hero_lead}</p>
          <div className="mt-8 lg:mt-10 flex flex-col sm:flex-row sm:items-center gap-3">
            <button data-testid="hero-primary-cta" onClick={() => selectTopic("einsteiger")} className="btn-accent">Kostenloses Probetraining anfragen</button>
            <a href="#kursfinder" data-testid="hero-finder-link" onClick={scrollToFinder} className="btn-ghost">Kurs finden <ArrowRight size={16} /></a>
          </div>
          <p data-testid="hero-facts" className="mt-5 text-[13px] md:text-sm muted">Kostenlos · unverbindlich · ohne Vorkenntnisse</p>
          <p data-testid="hero-corporate" className="mt-8 pt-6 border-t border-[#E6E3DD] text-sm text-[#3A3A3E]">
            <span className="font-head font-semibold text-[#161616]">Epassi oder Wellpass?</span> Bei uns trainierst du ohne Zusatzkosten.{" "}
            <a href="/firmenfitness/freischalten" data-testid="hero-corporate-unlock" className="tap inline-flex items-center gap-1 font-head font-semibold text-[#8A0093] hover:text-[#BA00C5] underline underline-offset-4 decoration-[#BA00C5]/40">Jetzt freischalten <ArrowRight size={14} /></a>
          </p>
        </div>
        {desktop && <HeroVisual reviews={reviews} />}
      </div>
      <TrustRow />
    </section>
  );
};

export const HeroFinder = () => {
  const { images } = useContent();
  const PATHS = buildPaths(images);
  const [who, setWho] = useState(null);
  const start = (id) => {
    setWho(id);
    setTimeout(() => document.getElementById("kursfinder-schritte")?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
  };
  return (
    <section data-testid="section-kursfinder" className="relative bg-white border-b border-[#E6E3DD]">
      <div id="kursfinder" className="relative max-w-[1320px] mx-auto px-5 md:px-10 pt-12 md:pt-16 pb-10 md:pb-14 scroll-mt-24">
        <div className="mb-6 sm:mb-8 max-w-2xl">
          <p className="eyebrow mb-2 sm:mb-3">Kurs und Termin finden · Frage 1 von 3</p>
          <h2 className="h-display text-2xl sm:text-3xl lg:text-4xl" data-testid="hero-paths-label">Was möchtest du für dich tun?</h2>
          <p className="mt-3 md:mt-4 text-sm md:text-base muted leading-relaxed" data-testid="hero-finder-intro">Tippe auf eine Karte. Nach zwei kurzen Fragen siehst du deinen Kurs mit den nächsten Terminen.</p>
        </div>
        <p data-testid="hero-swipe-hint" className="sm:hidden -mt-3 mb-3 text-xs muted">Wische zur Seite für alle vier Karten →</p>
        <div data-testid="hero-paths" className="snap-row sm:grid sm:grid-cols-2 lg:grid-cols-4 sm:gap-4" role="group" aria-label="Frage 1 von 3: Was möchtest du für dich tun?">{PATHS.map((p) => <PathCard key={p.id} p={p} active={who === p.id} onStart={start} />)}</div>
        <CourseFinder who={who} onReset={() => { setWho(null); requestAnimationFrame(() => document.getElementById("kursfinder")?.scrollIntoView({ behavior: "smooth", block: "start" })); }} />
      </div>
    </section>
  );
};

const TRUST = [["Berufsfeuerwehr Flensburg", "Offizieller Trainingspartner"], ["HYROX", "Training Club seit 2019"], ["ATHX", "Affiliate Gym"], ["Epassi", "Exklusiv-Studio in Flensburg"]];

const TrustItem = ({ name, sub, i }) => (
  <li data-testid={`hero-trust-${i}`} className="shrink-0 lg:shrink flex items-center gap-3 lg:gap-4">
    <span className="w-2 h-2 rounded-full bg-[#BA00C5] shrink-0" aria-hidden="true" />
    <span>
      <p className="font-head text-sm md:text-[15px] font-extrabold uppercase tracking-[0.08em] text-white leading-tight whitespace-nowrap">{name}</p>
      <p className="mt-0.5 text-xs md:text-[13px] text-white/60 whitespace-nowrap">{sub}</p>
    </span>
  </li>
);

const TrustRow = () => (
  <div className="relative bg-[#161616] overflow-hidden">
    <div className="hidden lg:flex max-w-[1320px] mx-auto px-10 py-7 items-center gap-10">
      <p className="shrink-0 font-head text-[11px] font-semibold uppercase tracking-[0.18em] text-white/45 pr-10 border-r border-white/15">Partner &amp; Auszeichnungen</p>
      <ul data-testid="hero-trust-row" aria-label="Partner und Auszeichnungen" className="flex-1 flex justify-between gap-x-8">
        {TRUST.map(([name, sub], i) => <TrustItem key={name} name={name} sub={sub} i={i} />)}
      </ul>
    </div>
    <div className="lg:hidden py-5 [mask-image:linear-gradient(to_right,transparent,black_8%,black_92%,transparent)]" aria-hidden="true">
      <ul className="marquee gap-10 px-5">{[...TRUST, ...TRUST].map(([name, sub], i) => <TrustItem key={`${name}-${i}`} name={name} sub={sub} i={`m-${i}`} />)}</ul>
    </div>
  </div>
);
